import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
  TextField
} from '@mui/material';
import {
  TrendingUp,
  DirectionsRun,
  FitnessCenter,
  ArrowForward,
  DirectionsBike,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, startOfMonth, differenceInDays } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// 1. IMPORT THE RTK QUERY HOOKS AND TYPES
import {
  useGetAnalyticsDashboardQuery,
  useGetActivitiesQuery,
  useGetUserEquipmentQuery,
  useGetUpcomingWorkoutsQuery,
  useGetSimpleWorkoutsQuery
} from '../../../store/api/baseApi';
// Define a type for the filter period
type FilterPeriod = 'week' | 'month' | 'custom';

/**
 * AthleteDashboardPage Component
 * 
 * Main athlete dashboard displaying overview metrics, recent activities,
 * upcoming workouts, and training load trends. Implements offline-first
 * strategy with cached data indicators.
 */
const AthleteDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('week');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [customEndDate, setCustomEndDate] = useState<Date | null>(new Date());

  // NEW: State for selected sport type
  const [selectedSport, setSelectedSport] = useState<string>('All');

  // Helper function to get UTC start of day
  const getUTCStartOfDay = (date: Date): Date => {
    const utcDate = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0, 0
    ));
    return utcDate;
  };

  // Helper function to get UTC end of day
  const getUTCEndOfDay = (date: Date): Date => {
    const utcDate = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23, 59, 59, 999
    ));
    return utcDate;
  };

  // 2. DETERMINE FILTER DATES BASED ON THE SELECTED PERIOD
  const getFilterDates = () => {
    const now = new Date();
    const utcNow = getUTCStartOfDay(now);
    
    switch (filterPeriod) {
      case 'week': {
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const utcWeekStart = getUTCStartOfDay(weekStart);
        return { after: utcWeekStart.toISOString() };
      }
      case 'month': {
        const monthStart = startOfMonth(now);
        const utcMonthStart = getUTCStartOfDay(monthStart);
        return { after: utcMonthStart.toISOString() };
      }
      case 'custom': {
        return {
          after: customStartDate ? getUTCStartOfDay(customStartDate).toISOString() : undefined,
          before: customEndDate ? getUTCEndOfDay(customEndDate).toISOString() : undefined,
        };
      }
      default: {
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const utcWeekStart = getUTCStartOfDay(weekStart);
        return { after: utcWeekStart.toISOString() };
      }
    }
  };

  // 3. CALCULATE THE NUMBER OF DAYS FOR THE DASHBOARD QUERY
  const getDaysForDashboard = () => {
    const now = new Date();
    const utcNow = getUTCStartOfDay(now);
    const MS_PER_DAY = 1000 * 60 * 60 * 24;

    if (filterPeriod === 'custom' && customStartDate && customEndDate) {
      const utcStart = getUTCStartOfDay(customStartDate);
      const utcEnd = getUTCStartOfDay(customEndDate);

      const days = Math.floor(
        (utcEnd.getTime() - utcStart.getTime()) / MS_PER_DAY
      );

      return Math.max(1, days);
    }

    switch (filterPeriod) {
      case 'week': {
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const utcWeekStart = getUTCStartOfDay(weekStart);

        const days = Math.floor(
          (utcNow.getTime() - utcWeekStart.getTime()) / MS_PER_DAY
        );

        return Math.max(1, days);
      }

      case 'month': {
        const monthStart = startOfMonth(now);
        const utcMonthStart = getUTCStartOfDay(monthStart);

        const days = Math.floor(
          (utcNow.getTime() - utcMonthStart.getTime()) / MS_PER_DAY
        );

        return Math.max(1, days);
      }

      default:
        return 90; // Fallback (already > 0)
    }
  };

  const filterDates = getFilterDates();
  const dashboardDays = getDaysForDashboard();

  // 4. USE THE DASHBOARD HOOK WITH THE DYNAMIC 'days' PARAMETER
  const { data: dashboardData, error: dashboardError, isLoading: isDashboardLoading } = useGetAnalyticsDashboardQuery({ days: dashboardDays });

  // 5. USE THE ACTIVITIES HOOK WITH THE DYNAMIC DATES
  const { data: activitiesData, error: activitiesError, isLoading: isActivitiesLoading } = useGetActivitiesQuery({
    ...filterDates,
    per_page: 50,
    page: 1,
  });

  // 6. USE THE EQUIPMENT HOOK
  const { data: equipmentData, error: equipmentError, isLoading: isEquipmentLoading } = useGetUserEquipmentQuery();

  // 7. USE THE UPCOMING WORKOUTS HOOK
  const { data: upcomingWorkoutsData, error: upcomingWorkoutsError, isLoading: isUpcomingWorkoutsLoading } = useGetUpcomingWorkoutsQuery({
    limit: 5,
    days_ahead: 7,
  });

  // 8. FETCH WORKOUTS TO MAP WORKOUT_ID TO WORKOUT NAME
  const { data: allWorkoutsData } = useGetSimpleWorkoutsQuery({});

  // Listeners for offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 8. DYNAMICALLY DERIVE SPORT OPTIONS FROM DASHBOARD DATA
  const sportOptions = React.useMemo(() => {
    if (!dashboardData?.volume_statistics?.by_sport_type) return ['All'];
    const keys = Object.keys(dashboardData.volume_statistics.by_sport_type);
    return ['All', ...keys];
  }, [dashboardData]);

  // 9. RETRIEVE STATS BASED ON SELECTED SPORT
  const currentVolumeStats = React.useMemo(() => {
    if (!dashboardData) return null;
    if (selectedSport === 'All' || !dashboardData.volume_statistics?.by_sport_type?.[selectedSport]) {
      return dashboardData.volume_statistics;
    }
    return dashboardData.volume_statistics.by_sport_type[selectedSport];
  }, [dashboardData, selectedSport]);

  const isLoading = isDashboardLoading || isActivitiesLoading || isEquipmentLoading;
  const error = dashboardError || activitiesError || equipmentError;

  const handlePeriodChange = (_event: React.MouseEvent<HTMLElement>, nextPeriod: FilterPeriod | null) => {
    if (nextPeriod !== null) {
      setFilterPeriod(nextPeriod);
    }
  };

  const handleSportChange = (_event: React.MouseEvent<HTMLElement>, nextSport: string | null) => {
    if (nextSport !== null) {
      setSelectedSport(nextSport);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !dashboardData) {
    const errorMessage = error && 'status' in error ? 'Failed to load dashboard.' : 'Dashboard data not found.';
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  // Helper to format distance (assuming meters in API -> km)
  const displayDistance = currentVolumeStats ? (currentVolumeStats.distance || currentVolumeStats.total_distance || 0) / 1000 : 0;

  // Helper to format pace from m/s to min/km
  const formatPace = (metersPerSecond?: number): string => {
    if (!metersPerSecond || metersPerSecond === 0) return '--:--';
    const secondsPerKm = 1000 / metersPerSecond;
    const minutes = Math.floor(secondsPerKm / 60);
    const seconds = Math.floor(secondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper to format speed from m/s to km/h
  const formatSpeed = (metersPerSecond?: number): string => {
    if (!metersPerSecond) return '--';
    return ((metersPerSecond * 3600) / 1000).toFixed(1);
  };

  // Helper to get workout name by ID
  const getWorkoutName = (workoutId?: number | null): string | null => {
    if (!workoutId || !allWorkoutsData) return null;
    const workout = allWorkoutsData.find(w => w.id === workoutId);
    return workout ? workout.name : null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {isOffline && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You are currently offline. Displaying cached data.
          </Alert>
        )}

        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>

        {/* Global Period Toggle */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <ToggleButtonGroup
              value={filterPeriod}
              exclusive
              onChange={handlePeriodChange}
              aria-label="Period selection"
              sx={{ width: '100%' }}
            >
              <ToggleButton value="week" sx={{ flex: 1 }}>This Week</ToggleButton>
              <ToggleButton value="month" sx={{ flex: 1 }}>This Month</ToggleButton>
              <ToggleButton value="custom" sx={{ flex: 1 }}>Custom Range</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {filterPeriod === 'custom' && (
            <Box display="flex" justifyContent="center" mt={2} gap={2}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                slots={{ textField: TextField }}
                slotProps={{ textField: { size: 'small' } }}
                enableAccessibleFieldDOMStructure={false}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                slots={{ textField: TextField }}
                slotProps={{ textField: { size: 'small' } }}
                enableAccessibleFieldDOMStructure={false}
              />
            </Box>
          )}
        </Paper>

        {/* NEW: Sport Type Toggle (Dynamic) */}
        <Paper elevation={1} sx={{ p: 2, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <ToggleButtonGroup
              value={selectedSport}
              exclusive
              onChange={handleSportChange}
              aria-label="Sport selection"
              sx={{ width: '100%', justifyContent: 'center' }}
            >
              {sportOptions.map((sport) => (
                <ToggleButton key={sport} value={sport} sx={{ px: 3 }}>
                  {sport}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {/* Overview Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Distance Card - Updated to use dashboard volume stats */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <DirectionsRun color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectedSport === 'All' 
                      ? (filterPeriod === 'week' ? "This Week's" : filterPeriod === 'month' ? "This Month's" : "Selected Period's")
                      : `${selectedSport}`} Distance
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {displayDistance.toFixed(1)} km
                </Typography>
                <Box display="flex" alignItems="center" mt={1}>
                  <TrendingUp fontSize="small" color="success" />
                  <Typography variant="caption" color="success.main" ml={0.5}>
                    Great progress!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* TSS Card - Updated to use dashboard training load summary */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <FitnessCenter color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Avg Weekly TSS (Last {dashboardDays} days)
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {dashboardData.training_load_summary.average_weekly_tss.toFixed(0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* CTL Card - Updated to use dashboard training load summary */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Current CTL (Fitness)
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {dashboardData.training_load_summary.current_ctl.toFixed(0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* TSB Card - Updated to use dashboard training load summary */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Training Stress Balance (TSB)
                </Typography>
                <Typography variant="h4" fontWeight="bold" color={dashboardData.training_load_summary.current_tsb > 0 ? 'success.main' : 'warning.main'}>
                  {dashboardData.training_load_summary.current_tsb > 0 ? '+' : ''}{dashboardData.training_load_summary.current_tsb.toFixed(0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Recent Activities */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Activities
                </Typography>
                <IconButton 
                  component={MuiLink} 
                  onClick={() => navigate('/athlete/activities')}
                  sx={{ color: 'primary.main' }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>

              {activitiesData?.activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No activities found for the selected period.
                </Typography>
              ) : (
                <Box>
                  {activitiesData?.activities.slice(0, 5).map((activity) => (
                    <Card
                      key={activity.id}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => navigate(`/athlete/activities/${activity.id}`)}
                    >
                      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {activity.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(activity.start_date), 'MMM dd, yyyy • HH:mm')}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {activity.sport_type}
                            </Typography>
                            {getWorkoutName(activity.workout_id) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                }}
                              >
                                {getWorkoutName(activity.workout_id)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Upcoming Workouts */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Upcoming Workouts
                </Typography>
                <IconButton
                  component={MuiLink}
                  onClick={() => navigate('/athlete/workouts')}
                  sx={{ color: 'primary.main' }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>

              {isUpcomingWorkoutsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress size={40} />
                </Box>
              ) : upcomingWorkoutsError ? (
                <Alert severity="error">
                  Failed to load upcoming workouts.
                </Alert>
              ) : !upcomingWorkoutsData || upcomingWorkoutsData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No upcoming workouts scheduled for the next 7 days.
                </Typography>
              ) : (
                <Box>
                  {upcomingWorkoutsData.map((workout) => (
                    <Card
                      key={workout.id}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => navigate(`/athlete/workouts/${workout.id}`)}
                    >
                      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {workout.name || workout.course?.name || 'Custom Workout'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {workout.scheduled_at
                                ? format(new Date(workout.scheduled_at), 'MMM dd, yyyy')
                                : 'No date set'}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor:
                                  workout.priority === 'high'
                                    ? 'error.main'
                                    : workout.priority === 'medium'
                                    ? 'warning.main'
                                    : 'info.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {workout.priority || 'normal'}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor:
                                  workout.status === 'completed'
                                    ? 'success.main'
                                    : workout.status === 'missed'
                                    ? 'error.main'
                                    : 'primary.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {workout.status}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Equipment Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                My Equipment
              </Typography>

              {isEquipmentLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress size={40} />
                </Box>
              ) : equipmentError ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Failed to load equipment data.
                </Alert>
              ) : equipmentData ? (
                <Grid container spacing={3}>
                  {/* Bikes Section */}
                  {equipmentData.bikes.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DirectionsBike sx={{ mr: 1 }} />
                        Bikes
                      </Typography>
                      <Grid container spacing={2}>
                        {equipmentData.bikes.map((bike) => (
                          <Grid item xs={12} sm={6} md={4} key={bike.id}>
                            <Card elevation={2}>
                              <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                  <Typography variant="h6" fontWeight="bold">
                                    {bike.name}
                                  </Typography>
                                  {bike.primary ? (
                                    <Star color="primary" fontSize="small" />
                                  ) : (
                                    <StarBorder color="disabled" fontSize="small" />
                                  )}
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Total Distance
                                  </Typography>
                                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                                    {(bike.distance / 1000).toFixed(0)} km
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Added: {format(new Date(bike.created_at), 'MMM dd, yyyy')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Last used: {format(new Date(bike.updated_at), 'MMM dd, yyyy')}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {/* Shoes Section */}
                  {equipmentData.shoes.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DirectionsRun sx={{ mr: 1 }} />
                        Running Shoes
                      </Typography>
                      <Grid container spacing={2}>
                        {equipmentData.shoes.map((shoe) => (
                          <Grid item xs={12} sm={6} md={4} key={shoe.id}>
                            <Card elevation={2}>
                              <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                  <Typography variant="h6" fontWeight="bold">
                                    {shoe.name}
                                  </Typography>
                                  {shoe.primary ? (
                                    <Star color="primary" fontSize="small" />
                                  ) : (
                                    <StarBorder color="disabled" fontSize="small" />
                                  )}
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Total Distance
                                  </Typography>
                                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                                    {(shoe.distance / 1000).toFixed(0)} km
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Added: {format(new Date(shoe.created_at), 'MMM dd, yyyy')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Last used: {format(new Date(shoe.updated_at), 'MMM dd, yyyy')}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {/* No equipment message */}
                  {equipmentData.bikes.length === 0 && equipmentData.shoes.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        No equipment found. Equipment will be automatically synced from your Strava account.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </LocalizationProvider>
  );
};

export default AthleteDashboardPage;